import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { VERSES, findVerse, gatewayIdForVerseEnv, gatewayUrlOf } from '@rb/shared/admin-verses';
import { GATEWAY_STORAGE_KEY, useActiveVerseStore } from './active-verse.store';

const STORAGE_KEY = 'rb-admin-app:active-verse';

export function VerseSelector() {
  const {
    manager,
    activeVerseId,
    switching,
    setActive,
    setSwitching,
    setConnectionError,
    setGatewayOverride,
  } = useActiveVerseStore();
  const [, setSearchParams] = useSearchParams();
  const [pendingProdAlias, setPendingProdAlias] = useState<string | null>(null);
  const active = activeVerseId ? findVerse(activeVerseId) : null;

  const performSwitch = async (alias: string) => {
    if (!manager) return;
    const target = findVerse(alias);
    if (!target) return;
    setSwitching(true);
    setConnectionError(null);
    // verse 변경 시 gateway override 해제 → 대상 env 의 gateway 로 연결.
    setGatewayOverride(null);
    window.localStorage.removeItem(GATEWAY_STORAGE_KEY);
    try {
      await manager.switchTo(target.alias, gatewayUrlOf(gatewayIdForVerseEnv(target.env)));
      setActive(target.verseId);
      window.localStorage.setItem(STORAGE_KEY, target.alias);
      setSearchParams((sp) => {
        const next = new URLSearchParams(sp);
        next.set('verse', target.alias);
        next.set('gateway', gatewayIdForVerseEnv(target.env));
        return next;
      });
    } catch (err) {
      console.error('[VerseSelector] switch failed:', err);
      setActive(null);
      setConnectionError(err instanceof Error ? err.message : String(err));
    } finally {
      setSwitching(false);
    }
  };

  const handleChange = (alias: string) => {
    const target = findVerse(alias);
    if (!target) return;
    if (target.env === 'prod') {
      setPendingProdAlias(alias);
      return;
    }
    void performSwitch(alias);
  };

  const pendingTarget = pendingProdAlias ? findVerse(pendingProdAlias) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">Verse</Label>
      <Select
        value={active?.alias ?? ''}
        disabled={!manager || switching}
        onValueChange={handleChange}
      >
        <SelectTrigger
          className={cn('w-full', active?.env === 'prod' && 'border-destructive text-destructive')}
        >
          <SelectValue placeholder="Connecting…" />
        </SelectTrigger>
        <SelectContent>
          {VERSES.map((v) => (
            <SelectItem key={v.alias} value={v.alias}>
              {v.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AlertDialog
        open={pendingProdAlias !== null}
        onOpenChange={(open) => {
          if (!open) setPendingProdAlias(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Switch to PRODUCTION verse?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to switch to <strong>{pendingTarget?.label}</strong>. Any action you
              take afterwards affects production data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingProdAlias) void performSwitch(pendingProdAlias);
                setPendingProdAlias(null);
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Switch to production
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
